/**
 * Card de UMA ação corretiva (GUPM-193, movido em GUPM-213). Cada ação tem responsável,
 * setor e prazo próprios (o modelo do `AcaoPlano`). Compartilhado pelo Novo Plano (MPA)
 * e pelo editor de ações da BAIXA da TARP. Sem PII versionada: responsável é papel
 * curado ou texto livre ("Outros") com aviso LGPD.
 */
import { PAPEIS_EXECUCAO, SETORES, prazoDiasUteis } from '@/domain/planos/mpa';
import { OUTROS_RESP, type AcaoDraft } from '@/domain/planos/acoes';
import { FAIXA_COLOR, inputCls, inputDarkCls, labelCls } from './estilos';
import { Field } from './Field';

interface Props {
  acao: AcaoDraft;
  index: number;
  /** Data de abertura do plano — referência para o prazo em dias úteis. */
  abertura: string;
  podeRemover: boolean;
  /** Modo FLAT (baixa da TARP): sem card em volta; campos escuros direto no bloco, para não
   *  criar "card dentro de card". Default false = card escuro com campos médios (Novo Plano). */
  flat?: boolean;
  onPatch: (patch: Partial<AcaoDraft>) => void;
  onRemove: () => void;
}

export function AcaoCard({ acao, index, abertura, podeRemover, flat = false, onPatch, onRemove }: Props) {
  const prazo = acao.conclusao ? prazoDiasUteis(abertura, acao.conclusao) : null;
  // FLAT (baixa): campos escuros direto no bloco médio — igual ao parecer, sem "card dentro
  // de card". CARD (Novo Plano): painel escuro (surface2) com campos médios por cima, como as
  // seções de dados do form de Troca de Plantão (GUPM-213, ajuste do dono).
  const fieldCls = flat ? inputDarkCls : inputCls;
  const containerCls = flat
    ? `col-span-2 ${index > 0 ? 'border-t border-jf-border pt-3' : ''}`
    : 'col-span-2 rounded-lg border border-jf-border bg-jf-surface2 p-3';
  return (
    <div className={containerCls}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-jf-muted">Ação {index + 1}</span>
        {podeRemover && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remover ação ${index + 1}`}
            className="text-[11px] font-bold text-level-emergencia transition hover:underline"
          >
            Remover ação
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Descrição da ação" span>
          <textarea
            className={`${fieldCls} min-h-[64px]`}
            value={acao.descricao}
            onChange={(e) => onPatch({ descricao: e.target.value })}
            placeholder="O que será feito"
            required
          />
        </Field>
        <Field label="Responsável pela execução">
          <select className={fieldCls} value={acao.responsavel} onChange={(e) => onPatch({ responsavel: e.target.value })}>
            {PAPEIS_EXECUCAO.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            <option value={OUTROS_RESP}>Outros…</option>
          </select>
        </Field>
        <Field label="Setor responsável">
          <select className={fieldCls} value={acao.setor} onChange={(e) => onPatch({ setor: e.target.value })}>
            {SETORES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        {acao.responsavel === OUTROS_RESP && (
          <Field label="Responsável (especificar)" span>
            <input
              className={fieldCls}
              value={acao.responsavelOutro}
              onChange={(e) => onPatch({ responsavelOutro: e.target.value })}
              placeholder="Nome ou função do responsável"
              maxLength={120}
              required
            />
            <span className="text-[10px] text-jf-dim">Use função ou nome; evite CPF e dados pessoais sensíveis (LGPD).</span>
          </Field>
        )}
        <Field label="Data máxima de conclusão">
          <input
            type="date"
            className={fieldCls}
            value={acao.conclusao}
            min={abertura}
            onChange={(e) => onPatch({ conclusao: e.target.value })}
            // GUPM-295: o prazo é obrigatório (`acaoValida`). O `required` é para o
            // navegador e leitores de tela anunciarem isso — o gate real é o domínio.
            required
          />
        </Field>
        <div className="text-[11px]">
          <span className={labelCls}>Prazo (dias úteis)</span>
          <div className="mt-1">
            {prazo == null ? (
              <span className="text-jf-dim">— defina a conclusão</span>
            ) : prazo.invalido ? (
              <span className="font-bold text-level-emergencia">Conclusão deve ser após a abertura</span>
            ) : (
              <span className="font-bold" style={{ color: FAIXA_COLOR[prazo.faixa] }}>
                {prazo.dias} dia(s) útil(eis)
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
